// src/lib/lazyImport.ts
// Wraps React.lazy to gracefully handle chunk-load failures right after a
// deployment, when the browser still holds an old index.html referencing
// chunk hashes that no longer exist on the server.
//
// The key insight: dynamic ES module import() does NOT go through
// window.fetch(), so patching fetch is useless. The only reliable fix when
// old chunk hashes are referenced is to reload the page with a URL cache-bust
// query parameter so that the browser fetches a fresh index.html (and thus
// fresh manifest / new chunk hashes) bypassing HTTP cache.
import React from 'react';

const DEPLOY_BUST_KEY = '__deploy_bust';
const RELOAD_TRACKER_KEY = 'chunk_reload_tracker_v2';
const MAX_AUTO_RELOADS = 3;
const RELOAD_WINDOW_MS = 2 * 60 * 1000;

type ReloadTracker = { ts: number; count: number };

function isChunkError(err: unknown): boolean {
  if (!err) return false;
  const msg: string = (err as any)?.message ?? String(err ?? '');
  return (
    /failed to fetch dynamically imported module/i.test(msg) ||
    /chunk/i.test(msg) ||
    /loading chunk/i.test(msg) ||
    /Importing a module script failed/i.test(msg) ||
    (err instanceof Error && err.name === 'TypeError' && /import/i.test(msg))
  );
}

function getTracker(): ReloadTracker {
  try {
    const raw = sessionStorage.getItem(RELOAD_TRACKER_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as ReloadTracker;
      if (typeof parsed.count === 'number' && typeof parsed.ts === 'number') {
        if (Date.now() - parsed.ts < RELOAD_WINDOW_MS) {
          return parsed;
        }
      }
    }
  } catch {
    // ignore storage errors
  }
  return { ts: Date.now(), count: 0 };
}

function canReload(): boolean {
  const t = getTracker();
  return t.count < MAX_AUTO_RELOADS;
}

function recordReload(): void {
  try {
    const cur = getTracker();
    const next: ReloadTracker = {
      ts: cur.count === 0 ? Date.now() : cur.ts,
      count: cur.count + 1,
    };
    sessionStorage.setItem(RELOAD_TRACKER_KEY, JSON.stringify(next));
  } catch {
    // ignore
  }
}

function deployBustReload(): void {
  try {
    recordReload();
  } catch {
    // ignore
  }
  try {
    const url = new URL(window.location.href);
    url.searchParams.set(DEPLOY_BUST_KEY, String(Date.now()));
    window.location.replace(url.toString());
  } catch {
    window.location.reload();
  }
}

export function lazyWithRetry<T extends React.ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
  displayName?: string,
): React.LazyExoticComponent<T> {
  const retryingFactory = async (): Promise<{ default: T }> => {
    try {
      return await factory();
    } catch (err: any) {
      if (isChunkError(err) && canReload()) {
        deployBustReload();
        // Intentionally never resolve — the page is about to unload anyway,
        // and we don't want React.lazy to re-throw into the ErrorBoundary
        // after having already triggered a reload.
        return new Promise<{ default: T }>(() => {});
      }
      throw err;
    }
  };

  const Lazy = React.lazy(retryingFactory);
  if (displayName) {
    (Lazy as any).displayName = displayName;
  }
  return Lazy;
}
